import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { adminResources, formatError } from "../api";
import { Card, PageHeader, Spinner } from "../ui";

export default function ResourcesList() {
  const [items, setItems] = useState(null);
  const load = () => adminResources.list().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm("Delete this resource?")) return;
    try { await adminResources.remove(id); toast.success("Deleted."); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  if (!items) return <Spinner />;
  return (
    <div data-testid="admin-resources-list">
      <PageHeader
        title="Resources"
        description="Publications, reports and downloadable files."
        actions={<Link to="/admin/resources/new" className="inline-flex items-center gap-2 rounded-lg bg-[hsl(var(--aoa-royal))] px-4 py-2 text-sm font-semibold text-white hover:bg-[hsl(var(--aoa-navy))]"><Plus className="h-4 w-4" /> New resource</Link>}
      />
      {items.length === 0 ? (
        <Card className="p-12 text-center"><p className="text-sm text-slate-500">No resources yet.</p></Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="min-w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <Th>Title</Th><Th>Category</Th><Th>Status</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <Td>
                    <div className="font-semibold text-[hsl(var(--aoa-navy))]">{r.title}</div>
                    <div className="text-xs text-slate-500 line-clamp-1">{r.description}</div>
                  </Td>
                  <Td className="text-sm text-slate-600">{r.category || "—"}</Td>
                  <Td><span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${r.published ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{r.published ? "Published" : "Hidden"}</span></Td>
                  <Td>
                    <div className="flex gap-2">
                      {r.file_url && <a href={r.file_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[hsl(var(--aoa-royal))]"><Download className="h-3.5 w-3.5" /></a>}
                      <Link to={`/admin/resources/${r.id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[hsl(var(--aoa-royal))]"><Pencil className="h-3.5 w-3.5" /></Link>
                      <button onClick={() => remove(r.id)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:border-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

const Th = ({ children }) => <th className="text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-600 px-5 py-3">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-5 py-4 text-sm align-middle ${className}`}>{children}</td>;
